import { Locales } from '@griply/types';
import { NLP } from 'shared/constants';

const RRULE_CODE_DAY_INDEX: Record<string, number> = {
  SU: 0,
  MO: 1,
  TU: 2,
  WE: 3,
  TH: 4,
  FR: 5,
  SA: 6,
};

const escapeForRegex = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Longest-first: a full name must beat its own abbreviation in both alternation and prefix lookup.
const byLengthDesc = (a: string, b: string) =>
  b.length - a.length || a.localeCompare(b);

// Every vocabulary read goes through these, so an unregistered locale degrades to English in one
// place instead of throwing at whichever table the caller happened to index directly.
export const getDayMap = (language: Locales) =>
  NLP.dayMap[language] ?? NLP.dayMap[Locales.EnUS];

export const getMonthMap = (language: Locales) =>
  NLP.MONTHS[language] ?? NLP.MONTHS[Locales.EnUS];

export const getRruleKeywords = (language: Locales) =>
  NLP.rruleKeywords[language] ?? NLP.rruleKeywords[Locales.EnUS];

export const getAtKeyword = (language: Locales) =>
  NLP.atKeywords[language] ?? NLP.atKeywords[Locales.EnUS];

const dayPatternCache = new Map<Locales, string>();
const monthPatternCache = new Map<Locales, string>();
const dayIndexCache = new Map<Locales, Record<string, number>>();

const toAlternation = (keys: string[]) =>
  `(?:${[...keys].sort(byLengthDesc).map(escapeForRegex).join('|')})`;

// `\b` is ASCII-only without the `u` flag, so it never fires around "środę", "mié" or "lunedì".
export const WORD_START = `(?<![^\\s,.;:!?({\\[\\u00A0\\u200B])`;
export const WORD_END = `(?=[\\s,.;:!?)\\]}\\u00A0\\u200B]|$)`;

export const getDayPattern = (language: Locales) => {
  const cached = dayPatternCache.get(language);
  if (cached) {
    return cached;
  }

  const dayMap = getDayMap(language);
  const pattern = `${toAlternation(Object.keys(dayMap))}${WORD_END}`;
  dayPatternCache.set(language, pattern);
  return pattern;
};

export const getMonthPattern = (language: Locales) => {
  const cached = monthPatternCache.get(language);
  if (cached) {
    return cached;
  }

  const months = getMonthMap(language);
  const pattern = toAlternation(Object.keys(months));
  monthPatternCache.set(language, pattern);
  return pattern;
};

// Keywords may be alternations, so a captured word is tested as a pattern, never compared by equality.
export const matchesKeyword = (value: string, keyword: string) =>
  new RegExp(`^(?:${keyword})$`).test(value);

// Locales whose keyword table defines `and` accept it as a list separator ("11. und 12.").
export const buildDayListSeparator = (and?: string) =>
  and ? `(?:,\\s*|\\s+${and}\\s+)` : `,\\s*`;

// Locales whose keyword table defines `of` accept a genitive particle in dates ("27 de enero").
export const buildDateSeparator = (of?: string) =>
  of ? `(?:\\s+${of})?\\s+` : `\\s+`;

export const getDayIndexMap = (language: Locales) => {
  const cached = dayIndexCache.get(language);
  if (cached) {
    return cached;
  }

  const dayMap = getDayMap(language);
  const indexMap = Object.keys(dayMap)
    .sort(byLengthDesc)
    .reduce<Record<string, number>>((acc, name) => {
      acc[name] = RRULE_CODE_DAY_INDEX[dayMap[name]];
      return acc;
    }, {});

  dayIndexCache.set(language, indexMap);
  return indexMap;
};
